import type { App } from '../../app/App';
import type { Topic } from '../../app/GameStore';
import { Scene } from '../core/Scene';
import type { ViewportInfo } from '../core/Viewport';
import { FighterView } from '../entities/FighterView';
import { acquireArt, type ArtHandle } from '../entities/art';
import { motion } from '../animation/Motion';
import { button } from '../../ui/components';
import { $, h, setChildren, waitAnim } from '../../ui/dom';
import { t, tn } from '../../i18n';
import { renderHeroPanel } from './notebook/heroPanel';
import { icon } from '../../ui/icons';
import { openFortuneTeller } from './notebook/fortune';
import { deskProps, paperScenery } from '../../ui/notebookArt';
import { decorateSkinTitles } from '../../ui/skin';
import { COLLECTION_PAGES, TAB_RENDERERS, type NotebookCtx, type TabId } from './notebook/tabs';
import { unansweredDares } from '../../networking/community';
import { requireHeroNames } from '../../ui/heroNames';
import { heroArtId, heroDrawing } from '../systems/hero';

/**
 * The bookmarks, in the order players need them: hero first, every monster
 * to fight right after it, the everyday loop (quests, shop, PVP, contest)
 * next, browsing and options last.
 */
const TABS: { id: TabId; label: string; color: string; icon: string }[] = [
  { id: 'hero', label: 'Heroes', color: 'yellow', icon: 'crown' },
  { id: 'wiki', label: 'Fight Monsters', color: 'purple', icon: 'slime' },
  { id: 'quests', label: 'Quests', color: 'orange', icon: 'mountain' },
  { id: 'equipment', label: 'Shop', color: 'blue', icon: 'coin' },
  { id: 'friends', label: 'PVP', color: 'pink', icon: 'swords' },
  { id: 'contest', label: 'Contest', color: 'cream', icon: 'trophy' },
  { id: 'monsters', label: 'Collection', color: 'paper', icon: 'book' },
  { id: 'settings', label: 'Options', color: 'green', icon: 'gear' },
];

/** Pages without a bookmark of their own light up the one they belong to. */
const PARENT_TAB: Partial<Record<TabId, TabId>> = {
  newhero: 'hero',
  class: 'friends',
  ...(Object.fromEntries(COLLECTION_PAGES.map((p) => [p.id, 'monsters'])) as Partial<Record<TabId, TabId>>),
};
const bookmarkOf = (id: TabId): TabId => PARENT_TAB[id] ?? id;
/** Position for the page-turn direction; collection sub-pages sit in strip order. */
const pageOrder = (id: TabId): number => TABS.findIndex((x) => x.id === bookmarkOf(id)) + Math.max(0, COLLECTION_PAGES.findIndex((p) => p.id === id)) / 10;

const TAB_TOPICS: Record<TabId, Topic[]> = {
  hero: ['hero'],
  newhero: [],
  monsters: ['monsters', 'hero'],
  wiki: ['monsters', 'hero'],
  equipment: ['inventory', 'hero'],
  quests: ['quests', 'hero', 'inventory'],
  memories: ['memories'],
  friends: ['friends', 'online'],
  // Loads from the server and re-renders itself.
  class: ['online'],
  // Loads from the server and re-renders itself; store changes would refetch.
  contest: ['online'],
  settings: ['settings', 'profile'],
};

/** SCREEN 02 — the Living Notebook hub: hero profile + tabbed pages. */
export class NotebookScene extends Scene<{ tab?: string } | void> {
  private tab: TabId = 'hero';
  private leftPage!: HTMLElement;
  private rightPage!: HTMLElement;
  private content!: HTMLElement;
  private tabsNav!: HTMLElement;
  private coins!: HTMLElement;
  private portrait: FighterView | null = null;
  private portraitArt: ArtHandle | null = null;
  private portraitKey = '';
  private flipping = false;
  private unsubTab: (() => void) | null = null;
  /** Pending incoming friend requests, shown on the Friends tab. */
  private friendRequests = 0;
  /** Undo functions registered by the rendered tab page (live listeners). */
  private pageCleanups: (() => void)[] = [];

  constructor(app: App) {
    super(app, 'scene-notebook');
  }

  enter(params: { tab?: string } | void): void {
    const app = this.app;
    const wanted = (params && params.tab) as TabId | undefined;
    // Opening the notebook always starts at the hero select (or drawing the first hero).
    this.tab = wanted && (wanted in TAB_RENDERERS) ? wanted : app.store.hero ? 'hero' : 'newhero';

    this.leftPage = h('div', { class: 'page left-page' });
    this.content = h('div', { class: 'page-content' });
    this.rightPage = h('div', { class: 'page right-page' }, this.content);
    this.tabsNav = h('nav', { class: 'paper-tabs', 'aria-label': t('Notebook sections') });
    this.coins = h('div', { class: 'coin-counter sticky-note yellow', title: t('Coins') });
    // The fortune teller (the prize draw) gets its own button, always in reach.
    const fortune = button(h('span', { class: 'btn-with-icon' }, icon('ticket'), t('Fortune')), { small: true, color: 'pink', title: t('Paper Fortune Teller'), sfx: app, onClick: () => void openFortuneTeller(app) });
    fortune.classList.add('nb-fortune');

    this.dom.append(
      h(
        'div',
        { class: 'nb-topbar' },
        button(t('← Menu'), { variant: 'ghost', small: true, sfx: app, onClick: () => void app.scenes.go('menu', undefined, 'page-back') }),
        h('div', { class: 'nb-title' }, t('My Notebook')),
        fortune,
        this.coins,
      ),
      h('div', { class: 'spread' }, this.leftPage, h('div', { class: 'spine', 'aria-hidden': 'true' }), this.rightPage, this.tabsNav),
      paperScenery('notebook-doodles'),
      deskProps(),
    );

    this.renderTabs();
    this.renderLeft();
    this.renderContent();
    this.renderCoins();

    this.d.add(app.store.subscribe(['hero', 'inventory'], () => this.renderLeft()));
    this.d.add(app.store.subscribe(['inventory'], () => this.renderCoins()));
    this.bindTabTopics();
    this.d.add(() => this.unsubTab?.());
    this.d.add(() => this.portraitArt?.release());
    this.d.add(() => this.cleanupPage());
    this.checkFriendRequests();
    // Old heroes still called by their class name get a real name first.
    void requireHeroNames(app);
    // Requests arrive live over the socket; the Friends page refreshes itself.
    for (const t of ['friend.request', 'friend.removed', 'friend.accepted', 'dare.received', 'dare.removed'] as const) this.d.add(app.mp.on(t, () => this.checkFriendRequests()));
  }

  private cleanupPage(): void {
    for (const fn of this.pageCleanups.splice(0)) fn();
  }

  /** Fetch the request count unless the Friends page (which keeps it fresh) is shown. */
  private checkFriendRequests(): void {
    const online = this.app.store.state.online;
    if (!online || this.tab === 'friends') return;
    this.app.api.token = online.token;
    this.app.api
      .friends()
      .then(async (res) => {
        const dares = await this.app.api.dares().catch(() => null);
        this.setFriendRequests(res.incoming.length + (dares ? unansweredDares(dares) : 0));
      })
      .catch(() => {});
  }

  private setFriendRequests(n: number): void {
    if (n === this.friendRequests) return;
    this.friendRequests = n;
    this.renderTabs();
  }

  private ctx(): NotebookCtx {
    return {
      app: this.app,
      goTab: (t) => void this.flipTo(t),
      refresh: () => this.renderContent(true),
      setFriendRequests: (n) => this.setFriendRequests(n),
      onCleanup: (fn) => this.pageCleanups.push(fn),
    };
  }

  private bindTabTopics(): void {
    this.unsubTab?.();
    const topics = TAB_TOPICS[this.tab];
    this.unsubTab = topics.length ? this.app.store.subscribe(topics, () => this.renderContent(true)) : null;
  }

  private renderTabs(): void {
    setChildren(
      this.tabsNav,
      TABS.map((tab, i) =>
        h(
          'button',
          {
            class: `paper-tab c-${tab.color} ${bookmarkOf(this.tab) === tab.id ? 'active' : ''}`,
            style: { '--i': String(i) } as unknown as Partial<CSSStyleDeclaration>,
            type: 'button',
            'aria-current': bookmarkOf(this.tab) === tab.id ? 'page' : undefined,
            on: { click: () => void this.flipTo(tab.id) },
            'aria-label':
              tab.id === 'friends' && this.friendRequests
                ? tn(this.friendRequests, '{label}, {n} new friend request', '{label}, {n} new friend requests', { label: t(tab.label) })
                : undefined,
          },
          icon(tab.icon, 'tab-icon'),
          h('span', { class: 'tab-label' }, t(tab.label)),
          tab.id === 'friends' && this.friendRequests ? h('span', { class: 'tab-badge', 'aria-hidden': 'true' }, String(this.friendRequests)) : null,
        ),
      ),
    );
  }

  private renderCoins(): void {
    setChildren(this.coins, icon('coin'), ` ${this.app.store.state.coins}`);
  }

  private renderLeft(): void {
    setChildren(this.leftPage, renderHeroPanel(this.ctx()));
    decorateSkinTitles(this.leftPage);
    this.syncPortrait();
  }

  /** Draws the current tab. A refresh of the same page (claiming a quest, buying) keeps the scroll position. */
  private renderContent(keepScroll = false): void {
    const top = keepScroll ? this.content.scrollTop : 0;
    this.cleanupPage();
    const el = TAB_RENDERERS[this.tab](this.ctx());
    setChildren(this.content, el);
    this.content.scrollTop = top;
    this.syncPortrait();
  }

  /** Turn the right-hand page to another tab. */
  async flipTo(tab: TabId): Promise<void> {
    if (tab === this.tab || this.flipping) {
      if (tab === this.tab) this.renderContent();
      return;
    }
    const forward = pageOrder(tab) > pageOrder(this.tab) || this.tab === 'newhero';
    this.flipping = true;
    this.app.quality?.pause(1500);
    this.app.sfx.play('flip');
    const sheet = h('div', { class: `flip-sheet page ${forward ? 'turn-forward' : 'turn-back'}` });
    if (forward) {
      // The current page turns over to the left, revealing the new one underneath.
      sheet.appendChild(this.content.cloneNode(true));
      sheet.firstElementChild?.scrollTo?.(0, this.content.scrollTop);
    }
    this.tab = tab;
    this.renderTabs();
    this.bindTabTopics();
    if (this.portrait) this.portrait.visible = false;
    if (!forward) {
      // Going back: the previous page swings back over from the left.
      this.renderContent();
      sheet.appendChild(this.content.cloneNode(true));
    }
    this.rightPage.appendChild(sheet);
    if (forward) this.renderContent();
    await waitAnim(sheet, motion.reduced ? 250 : 900);
    sheet.remove();
    this.flipping = false;
    // layout() (via syncPortrait) decides visibility: on phones only the hero tab has a portrait frame.
    this.syncPortrait();
  }

  /** Keep the animated hero portrait in sync with the hero and its anchor. */
  private syncPortrait(): void {
    const hero = this.app.store.hero;
    const key = hero ? `${hero.id}:${heroArtId(hero)}:${heroDrawing(hero)?.strokes.length ?? 0}` : '';
    if (key !== this.portraitKey) {
      this.portraitKey = key;
      if (this.portrait) {
        this.portrait.destroy({ children: true });
        this.portrait = null;
      }
      this.portraitArt?.release();
      this.portraitArt = null;
      if (hero) {
        this.portraitArt = acquireArt(this.app, heroDrawing(hero), heroArtId(hero));
        this.portrait = new FighterView(this.portraitArt, 1, this.app.tweens);
        this.root.addChild(this.portrait);
        void this.portrait.reveal();
      }
    }
    this.layout(this.app.viewport.info);
  }

  override layout(_vp: ViewportInfo): void {
    if (!this.portrait) return;
    // Use the visible anchor (left page in landscape, hero tab content on phones).
    const anchors = [...this.dom.querySelectorAll<HTMLElement>('.portrait-anchor')].filter((el) => el.offsetParent !== null && !el.closest('.flip-sheet'));
    const el = anchors[0];
    if (!el) {
      this.portrait.visible = false;
      return;
    }
    const a = this.anchor(el);
    const box = el.closest('.page')?.getBoundingClientRect();
    const inside = !box || (a.y + a.h * 0.5 > box.top && a.y + a.h * 0.5 < box.bottom);
    this.portrait.visible = !this.flipping && inside;
    this.portrait.position.set(a.cx, a.y + a.h * 0.97);
    this.portrait.setHeight(a.h * 0.92);
  }

  override update(dt: number): void {
    this.portrait?.update(dt);
  }

  override shown(): void {
    // Scroll containers move the anchor; re-layout on scroll (cheap, event-driven).
    const onScroll = (): void => this.layout(this.app.viewport.info);
    this.d.listen(this.content, 'scroll', onScroll, { passive: true });
    this.d.listen(this.leftPage, 'scroll', onScroll, { passive: true });
    this.layout(this.app.viewport.info);
    $(this.dom, '.paper-tab.active').focus({ preventScroll: true });
  }
}
